import type { Period, PeriodMode, WeekStart } from '../lib/period';
import { buildPeriod, customPeriod, shiftPeriod } from '../lib/period';

const MODES: { mode: PeriodMode; label: string }[] = [
  { mode: 'week', label: 'Week' },
  { mode: 'month', label: 'Month' },
  { mode: 'year', label: 'Year' },
  { mode: 'custom', label: 'Custom' },
];

export function PeriodSwitcher({
  period,
  weekStartsOn,
  onChange,
}: {
  period: Period;
  weekStartsOn: WeekStart;
  onChange: (period: Period) => void;
}) {
  const isCustom = period.mode === 'custom';

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div
        className="inline-flex rounded-[var(--radius-pill)] border border-[var(--color-line)] bg-white p-1"
        role="tablist"
        aria-label="Period"
      >
        {MODES.map(({ mode, label }) => {
          const active = period.mode === mode;
          return (
            <button
              key={mode}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() =>
                onChange(
                  mode === 'custom'
                    ? customPeriod(period.from, period.to)
                    : buildPeriod(mode, period.anchor, weekStartsOn),
                )
              }
              className={`rounded-[var(--radius-pill)] px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${
                active
                  ? 'bg-[var(--color-coral-500)] text-white'
                  : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-paper-sunk)]'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {isCustom ? (
        <div className="flex items-center gap-2">
          <input
            type="date"
            className="field !w-auto"
            value={period.from}
            max={period.to}
            onChange={(e) => onChange(customPeriod(e.target.value, period.to))}
            aria-label="From date"
          />
          <span className="text-[var(--color-ink-muted)]">–</span>
          <input
            type="date"
            className="field !w-auto"
            value={period.to}
            min={period.from}
            onChange={(e) => onChange(customPeriod(period.from, e.target.value))}
            aria-label="To date"
          />
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn btn-ghost !px-2.5"
            onClick={() => onChange(shiftPeriod(period, -1, weekStartsOn))}
            aria-label="Previous period"
          >
            ‹
          </button>
          <span className="min-w-[10.5rem] text-center font-display text-[15px] font-semibold">
            {period.label}
          </span>
          <button
            type="button"
            className="btn btn-ghost !px-2.5"
            onClick={() => onChange(shiftPeriod(period, 1, weekStartsOn))}
            aria-label="Next period"
          >
            ›
          </button>
          <button
            type="button"
            className="btn btn-ghost text-[12px]"
            onClick={() => onChange(buildPeriod(period.mode, new Date(), weekStartsOn))}
          >
            Today
          </button>
        </div>
      )}
    </div>
  );
}
